/**
 * Minimal, strongly-typed publish/subscribe event bus.
 *
 * `EventMap` is a record mapping event names to their payload types. Handlers are stored per event
 * and can be removed either via the returned unsubscribe function or {@link EventBus.off}.
 */
export type EventHandler<T> = (payload: T) => void;

export type EventMap = Record<string, unknown>;

export class EventBus<Events extends EventMap> {
  private readonly handlers = new Map<keyof Events, Set<EventHandler<never>>>();

  /**
   * Subscribe to an event. Returns an unsubscribe function that removes exactly this handler.
   */
  on<K extends keyof Events>(event: K, handler: EventHandler<Events[K]>): () => void {
    let set = this.handlers.get(event);
    if (!set) {
      set = new Set();
      this.handlers.set(event, set);
    }
    set.add(handler);
    return () => this.off(event, handler);
  }

  /** Subscribe to an event for a single emission, then auto-unsubscribe. */
  once<K extends keyof Events>(event: K, handler: EventHandler<Events[K]>): () => void {
    const off = this.on(event, (payload) => {
      off();
      handler(payload);
    });
    return off;
  }

  /** Remove a previously registered handler. No-op if it was not registered. */
  off<K extends keyof Events>(event: K, handler: EventHandler<Events[K]>): void {
    const set = this.handlers.get(event);
    if (!set) return;
    set.delete(handler);
    if (set.size === 0) {
      this.handlers.delete(event);
    }
  }

  /** Emit an event to all current subscribers. Iterates a snapshot so handlers may unsubscribe. */
  emit<K extends keyof Events>(event: K, payload: Events[K]): void {
    const set = this.handlers.get(event);
    if (!set) return;
    for (const handler of [...set]) {
      (handler as EventHandler<Events[K]>)(payload);
    }
  }

  /** Number of handlers registered for an event (primarily for tests/diagnostics). */
  listenerCount<K extends keyof Events>(event: K): number {
    return this.handlers.get(event)?.size ?? 0;
  }

  /** Remove every handler for one event, or all events when omitted. */
  clear<K extends keyof Events>(event?: K): void {
    if (event === undefined) {
      this.handlers.clear();
    } else {
      this.handlers.delete(event);
    }
  }
}
